import { createElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { SorareLogo } from 'atoms/icons/SorareLogo';
import { Container } from 'atoms/layout/Container';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { LinkOther } from 'atoms/navigation/Box';
import { SmallerStarBall } from 'atoms/navigation/SmallerStarBall';
import { ResponsiveSearchBar } from 'components/search/ResponsiveSearchBar';
import { LANDING } from 'constants/__generated__/routes';
import { useDefaultSportPages } from 'constants/routes';
import { useConnectionContext } from 'contexts/connection';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { glossary } from 'lib/glossary';
import { getFormattedSport, sportIcons } from 'lib/sports';
import { breakpoints, laptopAndAbove, mobileAndAbove } from 'style/mediaQuery';

const Wrapper = styled(SBHorizontal).attrs({ gap: 0 })`
  z-index: 1;
  position: relative;
  margin: auto;
  width: 100%;
  height: var(--app-header-height-mobile);
`;

const SportsLinks = styled.div`
  display: flex;
  flex: 1;
  gap: var(--unit);
`;

const Logo = styled(Link)`
  display: flex;
  gap: var(--unit);
  align-items: center;
  @media ${mobileAndAbove} {
    margin-right: var(--double-unit);
  }
`;

const SportButton = styled(Link)`
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: var(--unit);
  justify-content: space-between;
  @media ${laptopAndAbove} {
    padding: var(--unit);
    &:hover {
      border-radius: var(--unit);
    }
  }
`;

const MobileSportButtonsWrapper = styled(SBHorizontal)`
  padding: 0 0 var(--unit);
  overflow: auto;
`;

const ConnexionButtons = () => {
  const { signIn, signUp } = useConnectionContext();
  return (
    <Horizontal center>
      <Button size="small" color="primary" onClick={signUp}>
        <FormattedMessage {...glossary.signup} />
      </Button>
      <Button size="small" color="tertiary" onClick={signIn}>
        <FormattedMessage {...glossary.signin} />
      </Button>
    </Horizontal>
  );
};

const SportButtons = () => {
  const defaultSportPages = useDefaultSportPages();

  return [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL].map(sport => (
    <LinkOther as={SportButton} to={defaultSportPages[sport]} key={sport}>
      {getFormattedSport(sport)}
      {createElement(sportIcons[sport])}
    </LinkOther>
  ));
};

export const LandingAppHeader = () => {
  const { up: isMobile } = useScreenSize('mobile');
  const { up: isLaptop } = useScreenSize('laptop');
  return (
    <Container maxWidth={breakpoints.mediumDesktop}>
      <Vertical gap={0}>
        <Wrapper>
          <SportsLinks>
            <Logo to={LANDING} title="Sorare">
              {isMobile ? <SorareLogo /> : <SmallerStarBall />}
            </Logo>
            {!!isLaptop && <SportButtons />}
          </SportsLinks>
          <ResponsiveSearchBar />
          <ConnexionButtons />
        </Wrapper>
        {!isLaptop && (
          <MobileSportButtonsWrapper gap={2}>
            <SportButtons />
          </MobileSportButtonsWrapper>
        )}
      </Vertical>
    </Container>
  );
};
